import React from "react";
import api from '../../services/api';

/**
 * PaperFilesEdit Component
 * 
 * Enhanced file upload component for paper editing that shows existing files
 * and allows replacement with new files.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.paper - Original paper object with existing files
 * @param {Object} props.formData - Form data containing new file uploads
 * @param {Function} props.handleFileChange - Handler for file input changes
 */
const PaperFilesEdit = ({ paper, formData, handleFileChange }) => {
  /**
   * Formats file size from bytes to MB with 2 decimal places
   */
  const formatFileSize = (sizeInBytes) => {
    return (sizeInBytes / (1024 * 1024)).toFixed(2);
  };

  /**
   * Handle file download
   */
  const handleFileDownload = async (fileType) => {
    try {
      const response = await api.get(`/papers/${paper._id}/download/${fileType}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') 
          || paper.files[fileType]?.filename 
          || `${fileType}_${paper._id}.pdf`;
          
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  return (
    <div>
      <h4 className="mb-4">Manuscript Files</h4>

      {/* Full Manuscript Upload */}
      <div className="mb-4">
        <label htmlFor="fullManuscript" className="form-label">
          <i className="bi bi-file-earmark-pdf me-2"></i>
          Full Manuscript
        </label>

        {/* Show existing file */}
        {paper?.files?.fullManuscript && !formData.fullManuscript && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>Current File:</strong> {paper.files.fullManuscript.filename}
                  {paper.files.fullManuscript.size && (
                    <small className="text-muted ms-2">
                      ({formatFileSize(paper.files.fullManuscript.size)} MB)
                    </small>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleFileDownload('fullManuscript')}
                >
                  <i className="bi bi-download me-1"></i>
                  Download
                </button>
              </div>
            </div>
          </div>
        )}

        <input
          type="file"
          className="form-control"
          id="fullManuscript"
          name="fullManuscript"
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileChange}
        />
        <div className="form-text">
          {paper?.files?.fullManuscript && !formData.fullManuscript
            ? "Upload a new file to replace the current full manuscript (PDF or DOCX format, max 25MB)."
            : "Please upload your complete manuscript in PDF or DOCX format (max 25MB)."
          }
        </div>
      </div>

      {/* Blind Copy Manuscript Upload */}
      <div className="mb-4">
        <label htmlFor="blindCopyManuscript" className="form-label">
          <i className="bi bi-file-earmark-pdf me-2"></i>
          Blind Copy Manuscript
        </label>

        {/* Show existing blind copy file */}
        {paper?.files?.blindCopyManuscript && !formData.blindCopyManuscript && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>Current File:</strong> {paper.files.blindCopyManuscript.filename}
                  {paper.files.blindCopyManuscript.size && (
                    <small className="text-muted ms-2">
                      ({formatFileSize(paper.files.blindCopyManuscript.size)} MB)
                    </small>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleFileDownload('blindCopyManuscript')}
                >
                  <i className="bi bi-download me-1"></i>
                  Download
                </button>
              </div>
            </div>
          </div>
        )}

        <input
          type="file"
          className="form-control"
          id="blindCopyManuscript"
          name="blindCopyManuscript"
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileChange}
        />
        <div className="form-text">
          {paper?.files?.blindCopyManuscript && !formData.blindCopyManuscript
            ? "Upload a new file to replace the current blind copy manuscript (PDF or DOCX format, max 25MB)."
            : "Optional: Upload a blind copy of your manuscript with author information removed in PDF or DOCX format (max 25MB)."
          }
        </div>
      </div>

      {/* File Requirements Information */}
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        <strong>File Requirements:</strong>
        <ul className="mb-0 mt-2">
          <li>Files must be in PDF or DOCX format</li>
          <li>Maximum file size: 25MB per file</li>
          <li>Uploading new files will replace existing ones</li>
          <li>Leave file inputs empty to keep current files</li>
        </ul>
      </div>

      {/* Selected New Files Preview */}
      {(formData.fullManuscript || formData.blindCopyManuscript) && (
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="bi bi-files me-2"></i>
              New Files to Upload
            </h6>
          </div>
          <div className="card-body">
            {/* Full Manuscript File Info */}
            {formData.fullManuscript && (
              <div className="d-flex align-items-center mb-2">
                <i className={`bi ${formData.fullManuscript.name.toLowerCase().endsWith('.pdf') ? 'bi-file-earmark-pdf text-danger' : 'bi-file-earmark-word text-primary'} me-2`}></i>
                <span>
                  <strong>New Full Manuscript:</strong> {formData.fullManuscript.name}
                </span>
                <small className="text-muted ms-2">
                  ({formatFileSize(formData.fullManuscript.size)} MB)
                </small>
              </div>
            )}
            
            {/* Blind Copy Manuscript File Info */}
            {formData.blindCopyManuscript && (
              <div className="d-flex align-items-center">
                <i className={`bi ${formData.blindCopyManuscript.name.toLowerCase().endsWith('.pdf') ? 'bi-file-earmark-pdf text-danger' : 'bi-file-earmark-word text-primary'} me-2`}></i>
                <span>
                  <strong>New Blind Copy:</strong> {formData.blindCopyManuscript.name}
                </span>
                <small className="text-muted ms-2">
                  ({formatFileSize(formData.blindCopyManuscript.size)} MB)
                </small>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperFilesEdit;
